import { makeStyles } from "@mui/styles";
import { Button } from "components/Button/Button";
import { CancelButton } from "components/Form/components/CancelButton/CancelButton";
import NotificationService from "components/Notification/Notification.service";
import RestErrorNotification from "components/Notification/RestErrorNotification";
import { Popover } from "components/Popover/Popover";
import { actions as resourceActions } from "containers/Resources/Resources.reducer";
import { ResourceType } from "graphql/generated/graphql";
import { useState } from "react";
import { useDispatch } from "react-redux";
import restClient from "api/restClient";
import { getRelativeUrl, getTypedLowerCase } from "utils/service";
import {
    bytesToKb,
    computeRaiseToFitBytes,
} from "containers/Resources/screens/ResourcesSingle/components/ResourceDetails/maxTrackedSize";
import "./MaxTrackedSizeOverLimitDialog.scss";

/**
 * One tracked record that would exceed a candidate `max_tracked_record_bytes`
 * limit. Shape mirrors the backend `/git/oversized-tracked-records` response
 * (operator-task-fe-set-max-tracked-size-overlimit-check-modal).
 */
export interface OverLimitRecord {
    id: string;
    name: string;
    resource_type: string;
    size_bytes: number;
}

interface MaxTrackedSizeOverLimitDialogProps {
    isOpen: boolean;
    /** Container REST type ("project" | "bundle" | "workspace"). */
    resourceName: string;
    /** Id of the container whose limit is being changed. */
    containerId: string;
    /** container_type echoed by the backend (project | bundle | workspace). */
    containerType: string;
    /** The limit the user is trying to set, in bytes. */
    candidateBytes: number;
    /** Records currently over the candidate limit. */
    initialRecords: OverLimitRecord[];
    /** How many records the backend measured in this call (sizes not yet stored). */
    scanned: number;
    /** True when the backend stopped measuring early; the list may be incomplete. */
    truncated: boolean;
    /** When true the container enforces tracking, so untracking is not offered. */
    forceTrackInGit: boolean;
    /** Proceed and save the given limit (bytes), then close. */
    onSetLimit: (bytes: number) => void;
    /** Abort the limit change (resets the field) and close. */
    onCancel: () => void;
}

const toKb = bytesToKb;

/**
 * Pre-flight helper shown when a user lowers a container's tracked-record size
 * limit below the size of records that are already git-tracked. It lists the
 * offending records and offers proactive resolutions (untrack them, raise the
 * limit so they fit, or cancel) — it never mutates anything on its own; setting
 * the limit leaves existing records untouched (the guard only acts at the next
 * save of each record).
 *
 * Built on the canonical `Popover` dialog shell (ui-design-principle-modals-
 * dialogs: centered, mandatory bold header, no ×, Cancel-left / confirm-right,
 * 24px padding) with the shared `Button`/`CancelButton` per ui-design-principle-
 * buttons (one `primary`, co-equal actions `dark`).
 */
export const MaxTrackedSizeOverLimitDialog = ({
    isOpen,
    resourceName,
    containerId,
    containerType,
    candidateBytes,
    initialRecords,
    truncated,
    forceTrackInGit,
    onSetLimit,
    onCancel,
}: MaxTrackedSizeOverLimitDialogProps) => {
    const dispatch = useDispatch();
    const [records, setRecords] = useState<OverLimitRecord[]>(initialRecords);
    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [working, setWorking] = useState(false);

    const useStyles = makeStyles(() => ({
        rootStyle: {
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 2000,
        },
        paperStyle: {
            maxHeight: "80vh",
            overflow: "visible",
        },
    }));
    const classes = useStyles();

    const allSelected = records.length > 0 && selected.size === records.length;

    const toggle = (id: string) =>
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });

    const toggleAll = () =>
        setSelected(allSelected ? new Set() : new Set(records.map((record) => record.id)));

    const refresh = async () => {
        try {
            const response = await restClient.get(
                `${resourceName}/${containerId}/git/oversized-tracked-records`,
                { params: { by: "id", limit: candidateBytes } }
            );
            setRecords(response?.data?.over_limit ?? []);
            setSelected(new Set());
        } catch {
            // Keep the current list if the refresh call fails.
        }
    };

    const untrackSelected = () => {
        const toUntrack = records.filter((record) => selected.has(record.id));
        if (toUntrack.length === 0) {
            return;
        }
        setWorking(true);
        Promise.all(
            toUntrack.map(
                (record) =>
                    new Promise((resolve, reject) => {
                        dispatch({
                            type: resourceActions.START_UPDATE_RESOURCE,
                            promise: { resolve, reject },
                            resourceName: record.resource_type.toLowerCase(),
                            variables: { id: record.id, track_in_git: false },
                        });
                    })
            )
        )
            .then(() => {
                NotificationService.success(
                    `Untracked ${toUntrack.length} record${
                        toUntrack.length === 1 ? "" : "s"
                    } from git`
                );
                return refresh();
            })
            .catch((error) => {
                NotificationService.error(
                    <RestErrorNotification
                        titleOverride="Could not untrack record(s)"
                        error={error}
                    />
                );
            })
            .finally(() => setWorking(false));
    };

    const raiseBytes = computeRaiseToFitBytes(records, candidateBytes);

    const recordLabel = (record: OverLimitRecord) => {
        const url = getRelativeUrl(
            getTypedLowerCase(record.resource_type as ResourceType),
            record.id
        );
        return url ? (
            <a href={url} target="_blank" rel="noopener noreferrer" className="label">
                {record.name}
            </a>
        ) : (
            <span>{record.name}</span>
        );
    };

    return (
        <Popover
            isOpen={isOpen}
            classes={{ root: classes.rootStyle, paper: classes.paperStyle }}
            rootClassName="overLimitDialog__popover"
            contentClassName="overLimitDialog__content"
            anchorReference="none"
            closeOnInsideClick={false}
            closeOnOutsideClick={false}
        >
            <div className="overLimitDialog__header">Records exceed the new size limit</div>
            <div className="overLimitDialog__intro">
                Setting the tracked-record size limit for this {containerType} to{" "}
                {toKb(candidateBytes)} kB would leave the following git-tracked record
                {records.length === 1 ? "" : "s"} above the limit. Existing records are not
                changed &mdash; but the next time one of them is saved it will not be re-tracked in
                git while it stays over the limit. This limit applies to every record in the{" "}
                {containerType}.
            </div>
            {forceTrackInGit && (
                <div className="overLimitDialog__note">
                    Tracking is enforced for this {containerType} (force track in git), so these
                    records cannot be untracked here &mdash; raise the limit, or move a record to
                    another container, to keep it tracked.
                </div>
            )}
            {truncated && (
                <div className="overLimitDialog__note">
                    The check stopped before it had measured every tracked record, so there may be
                    more over the limit. Run the check again to continue: records measured so far
                    are not measured again.
                </div>
            )}
            <div className="overLimitDialog__tableWrap">
                <table className="overLimitDialog__table">
                    <thead>
                        <tr>
                            {!forceTrackInGit && (
                                <th className="overLimitDialog__checkCell">
                                    <input
                                        type="checkbox"
                                        checked={allSelected}
                                        onChange={toggleAll}
                                        disabled={working}
                                        aria-label="Select all records"
                                    />
                                </th>
                            )}
                            <th>Name</th>
                            <th>Type</th>
                            <th className="overLimitDialog__sizeCell">Size</th>
                        </tr>
                    </thead>
                    <tbody>
                        {records.map((record) => (
                            <tr key={record.id}>
                                {!forceTrackInGit && (
                                    <td className="overLimitDialog__checkCell">
                                        <input
                                            type="checkbox"
                                            checked={selected.has(record.id)}
                                            onChange={() => toggle(record.id)}
                                            disabled={working}
                                            aria-label={`Select ${record.name}`}
                                        />
                                    </td>
                                )}
                                <td>{recordLabel(record)}</td>
                                <td>{record.resource_type}</td>
                                <td className="overLimitDialog__sizeCell">
                                    {toKb(record.size_bytes)} kB
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div className="overLimitDialog__buttons">
                <CancelButton onClick={onCancel} />
                <div className="overLimitDialog__actionsRight">
                    {!forceTrackInGit && (
                        <Button
                            variation="dark"
                            label="Untrack selected"
                            disabled={selected.size === 0 || working}
                            loading={working}
                            onClick={untrackSelected}
                        />
                    )}
                    <Button
                        variation="dark"
                        label={`Raise limit to ${toKb(raiseBytes)} kB`}
                        disabled={working}
                        onClick={() => onSetLimit(raiseBytes)}
                    />
                    <Button
                        variation="primary"
                        label={`Set limit to ${toKb(candidateBytes)} kB`}
                        disabled={working}
                        onClick={() => onSetLimit(candidateBytes)}
                    />
                </div>
            </div>
        </Popover>
    );
};
